/**
 * <ambient-pause data-for="#SomeVideoId"> — WCAG 2.2.2 Pause, Stop, Hide (Level A).
 *
 * The homepage's ambient loops (the film band, the closing CTA) start automatically, run well past
 * five seconds, and sit in parallel with text. That is exactly the trigger for 2.2.2, which requires a
 * mechanism to pause, stop, or hide them.
 *
 * Two things that are easy to get wrong here, both learned the hard way on this build:
 *
 *  - `prefers-reduced-motion` does NOT discharge this obligation. Honouring it is necessary and we do,
 *    but 2.2.2 asks for a mechanism the visitor can reach IN the content. Someone who never set an OS
 *    preference still gets a vote.
 *  - The hero's "pause" button did not satisfy it either: it only froze the dot animation and never
 *    touched the video. A control that does not stop the moving thing is not a mechanism.
 *
 * Ownership: this element toggles the video directly AND stamps `video.__ambientPaused`. The players
 * (film-band.js, mux-bg.js) own their own play calls and re-play on scroll/visibility, so they MUST
 * consult that flag or they would simply undo the visitor's choice on the next sync.
 *
 * Per-instance on purpose, not a global motion preference: one button that silently stopped a video in
 * a different section would be more surprising than useful.
 *
 * IIFE + customElements.get guard: a section can render twice under the Section Rendering API, and a
 * top-level `class` would throw on the second evaluation.
 */
(() => {
  class AmbientPause extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('button');
      if (!this.btn) return;

      // The video is resolved by id rather than a descendant lookup: this control sits in the section's
      // content layer, while the video lives in the (aria-hidden) media layer, so it is not a child.
      this.video = this.dataset.for ? document.querySelector(this.dataset.for) : null;
      if (!this.video) {
        // No video on the page (merchant configured an image-only band): the control would be a lie.
        this.hidden = true;
        return;
      }
      // Testing for the ELEMENT is not enough. The players keep the <video> in the markup but refuse to
      // load it for save-data / 2g visitors (mux-bg.js, film-band.js), which left this button offering
      // to pause a stream that never existed. The player stamps __ambientUnavailable when it bails.
      // refresh() is public so the player can call it if it bails AFTER we upgrade (either order is
      // possible: custom element upgrade vs. the player's connectedCallback).
      if (this.refresh()) return;

      this.onClick = () => this.toggle();
      this.btn.addEventListener('click', this.onClick);

      // Keep the label honest if playback changes from anywhere else (the film modal pauses the ambient
      // loop while it is open, the players pause on scroll-away and tab-hide).
      this.onPlay = () => this.render();
      this.onPause = () => this.render();
      this.video.addEventListener('play', this.onPlay);
      this.video.addEventListener('pause', this.onPause);

      this.render();
    }

    disconnectedCallback() {
      this.btn?.removeEventListener('click', this.onClick);
      this.video?.removeEventListener('play', this.onPlay);
      this.video?.removeEventListener('pause', this.onPause);
    }

    /** Hide if the player has declared it will never load this stream. Returns true if hidden. */
    refresh() {
      if (!this.video) return false;
      const dead = !!this.video.__ambientUnavailable;
      this.hidden = dead;
      return dead;
    }

    toggle() {
      const paused = !this.video.__ambientPaused;
      this.video.__ambientPaused = paused;
      if (paused) this.video.pause();
      else this.video.play?.().catch(() => {});
      this.render();
    }

    render() {
      // aria-pressed tracks the visitor's INTENT (__ambientPaused), not the element's momentary paused
      // state. The players pause the video whenever it scrolls off screen; reflecting that would flip
      // the button to "play" for a video the visitor never paused and cannot currently see.
      const paused = !!this.video.__ambientPaused;
      this.btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      this.btn.setAttribute('aria-label', paused ? 'Play background video' : 'Pause background video');
      this.toggleAttribute('data-paused', paused);
    }
  }

  if (!customElements.get('ambient-pause')) customElements.define('ambient-pause', AmbientPause);
})();
